import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Copy, ExternalLink, Eye, Image, Link2, MessageSquareHeart, Palette, Save, Sparkles, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { celebrationService } from '@/lib/api/celebration.service';
import { guestService } from '@/lib/api/guest.service';
import { ImageUpload } from '@/components/ui/ImageUpload';
import { DEFAULT_THEME, TEMPLATE_PALETTE_SUGGESTIONS } from '@/lib/landing-theme-constants';
import { PalettePicker } from '@/components/guest-landing/PalettePicker';
import { FontPairingPicker } from '@/components/guest-landing/FontPairingPicker';
import { TemplatePicker } from '@/components/guest-landing/TemplatePicker';
import type { LandingThemeConfig } from '@/types/celebration.types';

export const GuestLandingConfigPage: React.FC = () => {
  const { id } = useParams();
  const { t } = useTranslation(['guests', 'common']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [slug, setSlug] = useState('');
  const [registrationToken, setRegistrationToken] = useState('');
  const [theme, setTheme] = useState<LandingThemeConfig>({ ...DEFAULT_THEME });
  const [savedTheme, setSavedTheme] = useState<LandingThemeConfig>({ ...DEFAULT_THEME });
  const [paletteNotice, setPaletteNotice] = useState('');
  const [generatingMessage, setGeneratingMessage] = useState(false);
  const themeChanged = theme.template_id !== savedTheme.template_id ||
    theme.palette_id !== savedTheme.palette_id ||
    theme.font_pairing_id !== savedTheme.font_pairing_id ||
    theme.hero_image_url !== savedTheme.hero_image_url ||
    theme.host_message !== savedTheme.host_message ||
    theme.greeting_template !== savedTheme.greeting_template;
  const guestLandingBaseUrl = (() => {
    const configuredBaseUrl = import.meta.env.VITE_GUEST_LANDING_BASE_URL?.trim();
    if (configuredBaseUrl) {
      return configuredBaseUrl.replace(/\/$/, '');
    }

    const { protocol, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5437`;
    }

    return window.location.origin;
  })();

  const celebrationId = id ? parseInt(id, 10) : 0;

  useEffect(() => {
    const load = async () => {
      if (!celebrationId || Number.isNaN(celebrationId)) {
        setError('Invalid celebration ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [celebration, guestDirectory] = await Promise.all([
          celebrationService.getCelebration(celebrationId),
          guestService.getCelebrationGuestDirectory(celebrationId),
        ]);
        setSlug(celebration.slug || '');
        setRegistrationToken(guestDirectory.registrationToken || '');
        const loaded: LandingThemeConfig = {
          template_id: celebration.landing_theme?.template_id ?? DEFAULT_THEME.template_id,
          palette_id: celebration.landing_theme?.palette_id ?? DEFAULT_THEME.palette_id,
          font_pairing_id: celebration.landing_theme?.font_pairing_id ?? DEFAULT_THEME.font_pairing_id,
          hero_image_url: celebration.landing_theme?.hero_image_url,
          host_message: celebration.landing_theme?.host_message,
          greeting_template: celebration.landing_theme?.greeting_template,
        };
        setTheme(loaded);
        setSavedTheme(loaded);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load guest landing configuration');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [celebrationId]);

  const guestUrl = slug ? `${guestLandingBaseUrl}/g/${slug}` : '';

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!celebrationId || Number.isNaN(celebrationId)) return;

    try {
      setSaving(true);
      setError(null);

      const normalizedSlug = slug.trim().toLowerCase();
      await celebrationService.updateCelebration(celebrationId, {
        slug: normalizedSlug || undefined,
      });

      setSlug(normalizedSlug);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save guest landing configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleThemeSave = useCallback(async () => {
    if (!celebrationId || Number.isNaN(celebrationId)) return;
    try {
      setSavingTheme(true);
      setError(null);
      await celebrationService.updateCelebration(celebrationId, {
        landing_theme: theme,
      });
      setSavedTheme({ ...theme });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save theme');
    } finally {
      setSavingTheme(false);
    }
  }, [celebrationId, theme]);

  const handleGenerateHostMessage = useCallback(async () => {
    if (!celebrationId || Number.isNaN(celebrationId)) return;
    try {
      setGeneratingMessage(true);
      setError(null);
      const message = await celebrationService.generateHostMessage(celebrationId);
      setTheme((prev) => ({ ...prev, host_message: message }));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to generate host message. Make sure an AI provider is configured.');
    } finally {
      setGeneratingMessage(false);
    }
  }, [celebrationId]);

  const previewUrl = slug && registrationToken
    ? `${guestLandingBaseUrl}/g/${slug}?preview=true&token=${encodeURIComponent(registrationToken)}&template=${theme.template_id}&palette=${theme.palette_id}&font=${theme.font_pairing_id ?? ''}`
    : '';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="h-5 w-5 animate-pulse" />
          <span>{t('guests:landing.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs />

      <header className="flex items-start gap-3 border-b pb-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Link2 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-semibold">{t('guests:landing.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Shape the page your guests see when they open a celebration link.</p>
        </div>
      </header>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      <Card className="border-primary/20">
        <CardContent className="p-5">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <Label htmlFor="slug">{t('guests:landing.labels.urlSlug')}</Label>
              <Input
                id="slug"
                name="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g., smith-wedding-2026"
                pattern="[a-z0-9-]+"
                title="Only lowercase letters, numbers, and hyphens"
                className="mt-1"
              />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={!previewUrl}
                onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
              >
                <Eye className="mr-2 h-4 w-4" />
                {t('guests:landing.preview')}
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="mr-2 h-4 w-4" />
                {saving ? t('guests:landing.saving') : t('guests:landing.save')}
              </Button>
            </div>

            {guestUrl && (
              <div className="rounded-md border bg-muted/30 p-3">
                <div className="flex items-center justify-between gap-2">
                  <a
                    href={guestUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline truncate"
                  >
                    {guestUrl}
                  </a>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(guestUrl, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(guestUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                    >
                      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquareHeart className="h-5 w-5" />
                {t('guests:landing.contentCard.title')}
              </CardTitle>
              <CardDescription className="mt-1">{t('guests:landing.contentCard.description')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              <Image className="h-4 w-4" />
              {t('guests:landing.labels.heroImage')}
            </Label>
            <ImageUpload
              value={theme.hero_image_url}
              onChange={(url) => setTheme((prev) => ({ ...prev, hero_image_url: url }))}
              placeholder={t('guests:landing.placeholders.heroImage')}
              helpText={t('guests:landing.helpText.heroImage')}
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              <MessageSquareHeart className="h-4 w-4" />
              {t('guests:landing.labels.hostMessage')}
            </Label>
            <div className="flex gap-2">
              <Textarea
                placeholder={t('guests:landing.placeholders.hostMessage')}
                value={theme.host_message || ''}
                onChange={(e) => setTheme((prev) => ({ ...prev, host_message: e.target.value || undefined }))}
                rows={3}
                className="flex-1"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={generatingMessage}
              onClick={() => void handleGenerateHostMessage()}
            >
              <Sparkles className="h-4 w-4 mr-1" />
              {generatingMessage ? t('guests:landing.generating') : t('guests:landing.generateAI')}
            </Button>
            <p className="text-xs text-muted-foreground">{t('guests:landing.helpText.hostMessage')}</p>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t('guests:landing.labels.greeting')}</Label>
            <Input
              placeholder={t('guests:landing.placeholders.greeting')}
              value={theme.greeting_template || ''}
              onChange={(e) => setTheme((prev) => ({ ...prev, greeting_template: e.target.value || undefined }))}
            />
            <p className="text-xs text-muted-foreground">Use <code className="bg-muted px-1 rounded">{'{guest_name}'}</code> as a placeholder — {t('guests:landing.helpText.greeting')}</p>
          </div>

        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div>
            <div>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                {t('guests:landing.appearanceCard.title')}
              </CardTitle>
              <CardDescription className="mt-1">{t('guests:landing.appearanceCard.description')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Template */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              {t('guests:landing.labels.layoutTemplate')}
            </Label>
            <TemplatePicker value={theme.template_id} onChange={(id) => {
              const suggestedPalette = TEMPLATE_PALETTE_SUGGESTIONS[id] || DEFAULT_THEME.palette_id;
              setTheme((prev) => ({ ...prev, template_id: id, palette_id: suggestedPalette }));
              setPaletteNotice('Suggested palette applied. You can choose another palette below.');
            }} />
          </div>

          <Separator />

          {/* Color Palette */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              <Palette className="h-4 w-4" />
              {t('guests:landing.labels.colorPalette')}
            </Label>
            <PalettePicker value={theme.palette_id} onChange={(id) => {
              setTheme((prev) => ({ ...prev, palette_id: id }));
              setPaletteNotice('');
            }} />
            {paletteNotice && <p role="status" className="text-xs text-muted-foreground">{paletteNotice}</p>}
          </div>

          <Separator />

          {/* Font Pairing */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              <Type className="h-4 w-4" />
              {t('guests:landing.labels.fontPairing')}
            </Label>
            <FontPairingPicker value={theme.font_pairing_id ?? 'playful'} onChange={(id) => setTheme((prev) => ({ ...prev, font_pairing_id: id }))} />
          </div>
        </CardContent>
      </Card>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          type="button"
          disabled={!themeChanged || savingTheme}
          onClick={() => void handleThemeSave()}
          className="shadow-lg"
        >
          <Save className="mr-2 h-4 w-4" />
          {savingTheme ? t('guests:landing.saving') : t('guests:landing.saveTheme')}
        </Button>
      </div>
    </PageContainer>
  );
};